import React, { useEffect, useState, useRef, useMemo } from 'react';
import Select from 'react-select';
import axios from 'axios';
import { selectUser } from '../../features/userSlice';
import styles from './UnloadingEntry.module.css';
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Truck, Building2, IdCard, Package, Scale } from "lucide-react";
import { usePopup } from '../../utils/PopupContext';
import InAppCamera from "../../components/InAppCamera";

const UnloadingEntry = () => {

  const [placeOptions, setPlaceOptions] = useState([]);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [truckList, setTruckList] = useState([]);
  const [selectedTruck, setSelectedTruck] = useState(null);
  const [unloadingMethod, setUnloadingMethod] = useState('Both');
  const [remarks, setRemarks] = useState('');
  //const [toastMessage, setToastMessage] = useState('');

  const [weightType] = useState('P');
  const [entityCode] = useState('SE');
  const topRef = useRef(null);

  const user = useSelector(selectUser);
  const emp_id = user?.emp_id;
  const token = user?.token;
  const navigate = useNavigate();
  const dispatch = useDispatch();

  //const [popup, setPopup] = useState({ open: false, title: '', message: '' });
  //const showPopup = (message, title = 'Notice') => setPopup({ open: true, title, message });
  //const closePopup = () => setPopup({ open: false, title: '', message: '' });
  const { showPopup } = usePopup();

  const SEPL_BLUE = "#1F6AA5";
  const SEPL_ORANGE = "#F39C12";

  // ---------------- Two-phase workflow + photos + GPS + geofence + existing photos ----------------
  const [phase, setPhase] = useState("before"); // "before" | "after"
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  
  const [gps, setGps] = useState({ lat: null, lng: null, accuracy: null, error: null });
  const [geoLoading, setGeoLoading] = useState(false);
  const isAutoSettingPlace = useRef(false); // prevent loops when auto-selecting LOV

  // geofence stations: [{ id, name, lat, lng, radiusMeters }]
  const [stations, setStations] = useState([]);
  const [autoStation, setAutoStation] = useState(null);
  const [selectionSource, setSelectionSource] = useState(null); // 'auto' | 'manual' | null

  const [qcRequired, setQcRequired] = useState('N');  // default
  const [qcLocked, setQcLocked] = useState(false);    // when true, user cannot change to N

  // inside component:
  const ua = (navigator.userAgent || "").toLowerCase();
  const isXiaomiLike = /miui|hyperos|redmi|xiaomi/.test(ua);
  const [useInAppCam, setUseInAppCam] = useState(isXiaomiLike); // default true for MIUI
  const [showInAppCam, setShowInAppCam] = useState(false);

  // Persist minimal state (so if MIUI kills the tab, you restore seamlessly)
  const saveCheckpoint = () => {
    try {
      const chk = {
        selectedPlace: selectedPlace ? { v: selectedPlace.value, l: selectedPlace.label, e: selectedPlace.entity_code } : null,
        selectedTruck: selectedTruck ? { doc: selectedTruck.p_docnumber } : null,
        phase, unloadingMethod, qcRequired, remarks
      };
      sessionStorage.setItem("UnloadChk", JSON.stringify(chk));
    } catch {}
  };

  const loadCheckpoint = () => {
    try {
      const s = sessionStorage.getItem("UnloadChk");
      if (!s) return;
      const chk = JSON.parse(s);
      if (chk?.selectedPlace && placeOptions.length) {
        const p = placeOptions.find(o => String(o.value) === String(chk.selectedPlace.v));
        if (p) setSelectedPlace(p);
      }
      if (chk?.selectedTruck && truckList.length) {
        const t = truckList.find(t => String(t.p_docnumber) === String(chk.selectedTruck.doc));
        if (t) setSelectedTruck(t);
      }
      if (chk?.phase) setPhase(chk.phase);
      if (chk?.unloadingMethod) setUnloadingMethod(chk.unloadingMethod);
      if (chk?.qcRequired) setQcRequired(chk.qcRequired);
      if (typeof chk?.remarks === "string") setRemarks(chk.remarks);
    } catch {}
  };

  const selectStyles = useMemo(() => ({
    control: (base, state) => ({
      ...base,
      backgroundColor: selectionSource === 'auto' ? '#f0fdf4' : '#fff', // light green when auto
      borderColor: selectionSource === 'auto' ? '#22c55e' : base.borderColor, // green border when auto
      boxShadow: 'none',
    }),
    singleValue: (base) => ({
      ...base,
      fontWeight: selectionSource === 'auto' ? 600 : base.fontWeight,
      backgroundColor: selectionSource === 'auto' ? '#dcfce7' : 'transparent',
      padding: selectionSource === 'auto' ? '2px 6px' : undefined,
      borderRadius: selectionSource === 'auto' ? 6 : undefined,
    }),
  }), [selectionSource]);
  
  // existing photos from DB for selected truck
  const [existing, setExisting] = useState({
    before_url: null,
    after_url: null,
    before_time: null,
    after_time: null,
    qc_required: null,     // ← add this
  });
  // -------------------------------------------------------------------------------------------------

  // Row.jsx (arrow fn + module classes)
  const Row = ({ icon, title, children, style }) => {
    return (
      <div className={styles.infoRow} style={style} title={title} aria-label={title}>
        <span className={styles.infoIcon} aria-hidden="true">{icon}</span>
        <span className={styles.infoValue}>{children}</span>
      </div>
    );
  }
    
  const formatKg = (n) => (Number(n)?.toLocaleString?.() ?? n) + " MT";

  /*
  const fetchUnloadingPlaces = async () => {
    const res = await axios.post('/api/wbauto/getUsersUnloadingStations', {
      emp_id,
      entity_code: entityCode,
      token,
    });
    const data = res.data || [];
    const formatted = data.map(row => ({ value: row.L_PlaceId, label: row.L_PlaceName,  }));
    setPlaceOptions(formatted);
  };
  */
  
  const fetchUnloadingPlaces = async () => {
    const res = await axios.post('/api/wbauto/getUsersUnloadingStations', {
      emp_id,
      token,
    });
    const data = res.data || [];
    console.log("Stage---0-",data);
    // Build LOV options (all returned rows)
    const formatted = data.map(row => ({
      value: row.L_PlaceId,
      label: row.L_PlaceName,
      entity_code: row.entity_code
    }));
    setPlaceOptions(formatted);
  
    // Build geofence list (only rows that have usable lat/lng)
    const geoList = data.map(x => ({
      id: x.id ?? x.L_PlaceId ?? x.code,
      name: x.name ?? x.L_PlaceName ?? x.label,
      entity_code: x.entity_code,
      lat: Number(x.lat),
      lng: Number(x.lng),
      radiusMeters: Number(x.radiusMeters ?? x.radius ?? 120)
    })).filter(x => Number.isFinite(x.lat) && Number.isFinite(x.lng));
    setStations(geoList);
  };
  
  const fetchTrucks = async ( placeId, entityForPlace) => {
    console.log("fetchTrucks Input: ", placeId, entityForPlace);
    if (!placeId) return;
    try {
      const res = await axios.post('/api/wbauto/getUnloadTrucks', {
        emp_id,
        entity_code: entityForPlace ?? entityCode,
        placeId,
        weightype: weightType,
        token,
      });
      console.log(res.data);
      setTruckList(res.data || []);
      setSelectedTruck(null);
      setUnloadingMethod('Both');
      setRemarks('');
      setExisting({ before_url: null, after_url: null, before_time: null, after_time: null });

      //setPhase("before");
      if (photoPreview) URL.revokeObjectURL(photoPreview);
      setPhotoFile(null);
      setPhotoPreview(null);
            
    } catch (err) {
      console.error('getUnloadTrucks error:', err);
      setTruckList([]);
      showPopup(err?.response?.data?.message || 'Failed to load trucks.', 'Error');
    }    
  };

  const effectiveEntityCode = selectedTruck?.entity_code || selectedPlace?.entity_code || entityCode;

  useEffect(() => {
    if (selectedPlace) {
      console.log("Stage-1-", selectedPlace.value, selectedPlace.entity_code);
      fetchTrucks(selectedPlace.value, selectedPlace.entity_code); // ✅ correct
    } else {
      setTruckList([]);
    }
  }, [selectedPlace]);
  
  const getTruckOption = (truck) => {
    if (!truck) return null;
    return {
      value: truck.p_docnumber,
      label: `${truck.P_truck} - ${truck.p_item_name}`,
      data: truck,
    };
  };

  // after your first fetches (e.g., after trucks load), try restoring once:
  //useEffect(() => { loadCheckpoint(); /* run once after options/trucks available */ }, [placeOptions.length, truckList.length]);
  const didRestoreRef = useRef(false);
  useEffect(() => {
    if (!didRestoreRef.current && placeOptions.length >= 0) {
      loadCheckpoint();
      didRestoreRef.current = true;
    }
  }, [placeOptions.length]);
 

  // ---------------- Helpers: image + GPS + geofence + existing photos ----------------
  const getLocation = () =>
    new Promise((resolve, reject) => {
      if (!navigator.geolocation) return reject(new Error("Geolocation not supported"));
      navigator.geolocation.getCurrentPosition(
        pos => resolve({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy
        }),
        err => reject(err),
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    });

  const readImageFile = (file) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      const fr = new FileReader();
      fr.onload = () => { img.onload = () => resolve({ img, url: fr.result }); img.src = fr.result; };
      fr.onerror = reject;
      fr.readAsDataURL(file);
    });

  const compressToJpegBlob = async (file, maxSide = 1280, quality = 0.8) => {
    const { img } = await readImageFile(file);
    const scale = Math.min(1, maxSide / Math.max(img.width, img.height));
    const w = Math.round(img.width * scale);
    const h = Math.round(img.height * scale);
    const canvas = document.createElement("canvas");
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, w, h);
    return await new Promise(res => canvas.toBlob(b => res(b), "image/jpeg", quality));
  };

  const onPhotoPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const blob = await compressToJpegBlob(f);
    const name = phase === 'after' ? 'after.jpg' : 'before.jpg';
    const file = new File([blob], name, { type: 'image/jpeg' });
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  };
  
  useEffect(() => {
    return () => {
      if (photoPreview) URL.revokeObjectURL(photoPreview);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // avoid submitting a before image while in "after" phase or vice-versa
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhotoFile(null);
    setPhotoPreview(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);
    
  // Haversine (meters)
  const haversine = (a, b) => {
    if (!a?.lat || !a?.lng || !b?.lat || !b?.lng) return Infinity;
    const toRad = (x) => (x * Math.PI) / 180;
    const R = 6371000;
    const dLat = toRad(b.lat - a.lat);
    const dLon = toRad(b.lng - a.lng);
    const lat1 = toRad(a.lat);
    const lat2 = toRad(b.lat);
    const s = Math.sin(dLat/2)**2 + Math.cos(lat1)*Math.cos(lat2)*Math.sin(dLon/2)**2;
    return 2 * R * Math.asin(Math.sqrt(s));
  };

  const detectStationByGeo = (lat, lng, stationsList) => {
    if (!lat || !lng || !stationsList?.length) return null;
    let best = null;
    for (const st of stationsList) {
      const d = haversine({lat, lng}, {lat: st.lat, lng: st.lng});
      if (d <= st.radiusMeters && (!best || d < best.dist)) best = { ...st, dist: d };
    }
    return best; // or null
  };

  /*
  const fetchStationsGeo = async () => {
    try {
      const res = await axios.post('/api/wbauto/getUnloadingStationsGeo', {
        entity_code: entityCode,
        token
      });
      const list = (res.data || []).map(x => ({
        id: x.id ?? x.L_PlaceId ?? x.code,
        name: x.name ?? x.L_PlaceName ?? x.label,
        lat: Number(x.lat),
        lng: Number(x.lng),
        radiusMeters: Number(x.radiusMeters ?? x.radius ?? 120)
      })).filter(x => x.lat && x.lng);
      setStations(list);
    } catch (e) {
      console.warn("getUnloadingStationsGeo failed; geofence fallback to manual select.");
      setStations([]);
    }
  };
  */

  const fetchExistingPhotos = async (docnumber) => {
    if (!docnumber) return;
    try {
      // Expecting { before_url, after_url, before_time, after_time }
      const res = await axios.post('/api/wbauto/getUnloadEvents', {
        docnumber,
        entity_code: effectiveEntityCode,
        token
      });
      // normalizers
      const ALLOWED = new Set(['Labour','Machine','Both','Self']);
      const pickMethod = (v) => {
        const s = typeof v === 'string' ? v.trim() : '';
        return ALLOWED.has(s) ? s : 'Both';
      };
      const pickYN = (v) => (String(v || '').trim().toUpperCase() === 'Y' ? 'Y' : 'N');


      console.log("Data Stage-1a:",res.data);
      const d = res.data || {};
      setExisting({
        before_url: d.before_url || null,
        after_url: d.after_url || null,
        before_time: d.before_time || null,
        after_time: d.after_time || null,
        unloading_method:d.unloading_method ?? null,
        remarks: d.remarks || null,
        qc_required: d.qc_required ?? null,   // ← keep as 'Y'/'N'/null
      });
      
      // 👇 update the actual form controls
      setUnloadingMethod(pickMethod(d.unloading_method));
      setQcRequired(pickYN(d.qc_required));
      setRemarks(d.remarks || '');

      if (d.before_url && !d.after_url) setPhase("after");
      // Prefer AFTER if it exists; otherwise if only BEFORE exists, default to AFTER; else BEFORE.
      const nextPhase = d.after_url ? 'after' : (d.before_url ? 'after' : 'before');
      setPhase(nextPhase);
      
    } catch (e) {
      console.warn("getUnloadEvents failed; continuing without existing photos.");
      setExisting({ before_url: null, after_url: null, before_time: null, after_time: null });
      setUnloadingMethod('Both');  // fall back
      setQcRequired('N');
      setRemarks('');      
    }
  };
  // ----------------------------------------------------------------------------------------------------

// replace your existing captureGpsNow with this version
const captureGpsNow = async (mode = 'silent', opts = {}) => {
  const { onlyIfEmpty = false } = opts;   // 👈 new option
  setGeoLoading(true);
  try {
    const g = await getLocation();
    setGps({ ...g, error: null });

    if (Number.isFinite(g.lat) && Number.isFinite(g.lng) && stations.length) {
      const det = detectStationByGeo(g.lat, g.lng, stations);
      setAutoStation(det || null);

      if (mode === 'refresh' && det) {
        // honor only-if-empty: don't override user’s manual choice
        const allowAuto =
          !onlyIfEmpty || (!selectedPlace && selectionSource !== 'manual');

        if (allowAuto) {
          const exists = placeOptions.some(o => o.value === det.id);
          if (!exists) {
            setPlaceOptions(prev => [...prev, { value: det.id, label: det.name, entity_code: det.entity_code }]);
          }
          if (!selectedPlace || selectedPlace.value !== det.id) {
            isAutoSettingPlace.current = true;
            setSelectedPlace({ value: det.id, label: det.name, entity_code: det.entity_code });
            setSelectionSource('auto');
            setTimeout(() => { isAutoSettingPlace.current = false; }, 0);
          } else {
            setSelectionSource('auto'); // just keep the green styling
          }
        }
      }
    }
    return g;
  } catch (err) {
    const e = { lat: null, lng: null, accuracy: null, error: err?.message || 'GPS unavailable' };
    setGps(e);
    return e;
  } finally {
    setGeoLoading(false);
  }
};

  // GPS capture that ALSO auto-selects geofenced station in LOV and returns coords
  
  // On mount: load LOV + geofences, then capture GPS once
  useEffect(() => {
    (async () => {
      await fetchUnloadingPlaces();   // sets placeOptions + stations asynchronously
      await captureGpsNow('silent');  // just capture GPS; don't touch LOV
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Auto-select only on first load (when nothing chosen yet and user didn't pick manually)
    if (stations.length > 0 && !selectedPlace && selectionSource !== 'manual') {
      captureGpsNow('refresh', { onlyIfEmpty: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stations]);
  

  // When truck changes: recapture GPS and load existing photos
  useEffect(() => {
    (async () => {
      if (selectedTruck) {
        await fetchExistingPhotos(selectedTruck.p_docnumber);

        // clear staged photo when changing truck
        if (photoPreview) URL.revokeObjectURL(photoPreview);
        setPhotoFile(null);
        setPhotoPreview(null);

      } else {
        setExisting({ before_url: null, after_url: null, before_time: null, after_time: null });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTruck]);

  useEffect(() => {
    (async () => {
      if (!selectedTruck) { setQcLocked(false); return; }
  
      try {
        const res = await axios.post('/api/wbauto/isQcMandatory', {
          entity_code: effectiveEntityCode,
          docnumber: selectedTruck.p_docnumber,
          token
        });
        const mandatory = !!res.data?.mandatory;
        setQcLocked(mandatory);
        if (mandatory) setQcRequired('Y');   // default to Yes
      } catch (e) {
        console.warn('isQcMandatory failed:', e?.message || e);
        setQcLocked(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTruck, effectiveEntityCode]);
  
  // ❌ Removed GPS recapture on LOV change to avoid loops

  const handleSubmit = async () => {
    // --- quick helpers (scoped to this function) ---
    const asStr = (v, maxLen) => {
      if (v === undefined || v === null) return '';
      // unwrap boxed values (e.g., new String())
      if (typeof v === 'object' && v.valueOf && typeof v.valueOf() !== 'object') v = v.valueOf();
      const s = String(v);
      return maxLen ? s.slice(0, maxLen) : s;
    };
    const asNum = (v) => {
      if (v === undefined || v === null || v === '') return null;
      if (typeof v === 'object' && v.valueOf && typeof v.valueOf() !== 'object') v = v.valueOf();
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    };
    const asInt = (v) => {
      const n = asNum(v);
      return n === null ? null : Math.trunc(n);
    };
  
    // --- validations ---
    if (!selectedTruck || !unloadingMethod) {
      showPopup({
        variant: 'warning',
        title: 'Missing info',
        message: 'Please select a truck and unloading method.'
      });
      return;
    }
    
    if (!selectedPlace && !autoStation) {
      showPopup({
        variant: 'info',
        title: 'Unloading Place',
        message: 'Select an Unloading Place or tap Refresh GPS so we can auto-pick one.'
      });
      return;
    }
    
    if (phase === 'before' && !photoFile && !existing.before_url) {
      showPopup({
        variant: 'warning',
        title: 'Photo required',
        message: 'Attach the BEFORE photo.'
      });
      return;
    }
    
    if (phase === 'after' && !photoFile && !existing.after_url) {
      showPopup({
        variant: 'warning',
        title: 'Photo required',
        message: 'Attach the AFTER photo.'
      });
      return;
    }
          
    const ok = window.confirm(`Confirm ${phase.toUpperCase()} submission for Truck: ${selectedTruck.P_truck}?`);
    if (!ok) return;
  
    // --- final GPS snapshot ---
    const finalGeo = await captureGpsNow('silent');
  
    // --- choose station (manual > auto) ---
    let chosenLocationCode = selectedPlace?.value ?? "";
    let chosenLocationName = selectedPlace?.label ?? "";
    if (finalGeo?.lat && stations.length) {
      const det = detectStationByGeo(finalGeo.lat, finalGeo.lng, stations);
      setAutoStation(det || null);
      if (det && !selectedPlace?.value) {
        chosenLocationCode = det.id;
        chosenLocationName = det.name;
      }
    }
  
    // --- prefer entity from TRUCK, then STATION, then fallback state 'entityCode' ---
    const stationForSelected =
      stations.find(s => String(s.id) === String(selectedPlace?.value)) || null;
  
    const effectiveEntityCode =
      selectedTruck?.entity_code ||
      stationForSelected?.entity_code ||
      entityCode || 'SE';
  
    // --- sanitize to primitives with DB-aligned lengths ---
    const clean = {
      entity_code:      asStr(effectiveEntityCode, 10),
      docnumber:        asStr(selectedTruck?.p_docnumber, 50),
      truck_no:         asStr(selectedTruck?.P_truck, 32),
      phase:            (phase === 'after' ? 'after' : 'before'),
      locationCode:     asInt(chosenLocationCode),             // INT or null
      locationName:     asStr(chosenLocationName, 120),
      lat:              asNum(finalGeo?.lat) !== null ? asNum(finalGeo.lat).toFixed(6) : '',
      lng:              asNum(finalGeo?.lng) !== null ? asNum(finalGeo.lng).toFixed(6) : '',
      accuracy:         asInt(finalGeo?.accuracy) ?? '',
      unloading_method: asStr(unloadingMethod, 20),
      qc_required:      (qcRequired === 'Y' ? 'Y' : 'N'),
      remarks:          asStr(remarks, 1000),
      emp_id:           asStr(emp_id, 32),
    };
  
    // --- front-end guard for requireds (prevents NULL into SP) ---
    if (!clean.entity_code || !clean.docnumber || !clean.truck_no) {
      showPopup({
        variant: 'warning',
        title: 'Validation',
        message: 'Missing entity_code / docnumber / truck_no. Please reselect the truck and try again.'
      });
      return;
    }
      
    try {
      // --- build FormData from sanitized values only ---
      const fd = new FormData();
      Object.entries({
        locationCode: clean.locationCode,
        locationName: clean.locationName,
        docnumber: clean.docnumber,
        truck_no: clean.truck_no,
        unloading_method: clean.unloading_method,
        qc_required:      clean.qc_required,
        remarks: clean.remarks,
        emp_id: clean.emp_id,
        entity_code: clean.entity_code,
        phase: clean.phase,
        lat: clean.lat,
        lng: clean.lng,
        accuracy: clean.accuracy,
        token, // keep if backend still expects it
      }).forEach(([k, v]) => fd.append(k, v ?? ''));
  
      if (photoFile) fd.append("photo", photoFile);

  
      const res = await axios.post('/api/wbauto/updateUnloadingStatus', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
  
      if (res.data?.success) {
        showPopup({
          variant: 'success',
          title: 'Saved',
          message: `${phase.toUpperCase()} submission saved.`,
          autocloseMs: 1500
        });

        if (photoPreview) URL.revokeObjectURL(photoPreview);
        setPhotoFile(null);
        setPhotoPreview(null);
        
        // refresh list + existing photos
        //fetchTrucks(selectedPlace?.value);
        //await fetchExistingPhotos(selectedTruck.p_docnumber);
        
        // Refresh trucks for the same place & entity; don't read stale selectedTruck
        if (selectedPlace) {
          await fetchTrucks(selectedPlace.value, selectedPlace.entity_code);
        } else {
          setTruckList([]);
        }        
      } else {
        const msg = res.data?.message || 'Failed to update status.';
        showPopup({ variant: 'error', title: 'Error', message: msg });
      }
    } catch (error) {
      console.error('Submit error:', error);
      const msg = error?.response?.data?.message || 'Error submitting data.';
      showPopup({ variant: 'error', title: 'Error', message: msg });
    }
  };
    
  if (!token) {
    return (
      <div>
        <p>You must be logged in to register a employee.</p>
        <button onClick={() => navigate("/")}>Go to Login</button>
      </div>
    );
  }

  return (
    <div className={styles.approveContainer}>
      <div ref={topRef} />
      <h2 className={styles.screenTitle}>Truck Unloading Entry</h2>
      <div className={styles.gpsRow}>
        <div>
          <strong>GPS: </strong>
          {geoLoading ? "Fetching…" :
            gps.lat ? `lat ${gps.lat.toFixed(6)}, lng ${gps.lng.toFixed(6)} (±${Math.round(gps.accuracy||0)}m)` :
            gps.error ? `Error: ${gps.error}` : 'Awaiting permission...'}
        </div>
        {/* Optional refresh; auto-capture already covers open, truck change, and submit */}
        {/*<button type="button" className={styles.gpsBtn} onClick={() => captureGpsNow('refresh')}  title="Refresh location"> */}
        <button type="button" className={styles.gpsBtn} onClick={() => captureGpsNow('refresh', { onlyIfEmpty: true })} title="Refresh location">
          Refresh
        </button>
      </div>

      {gps.lat && stations.length > 0 && (
        <div className={styles.geoHint}>
          {(() => {
            const det = detectStationByGeo(gps.lat, gps.lng, stations);
            return det ? (
              <span>📍 Auto-selected station: <strong>{det.name}</strong> ({Math.round(det.dist)}m)</span>
            ) : (
              <span>📍 Outside any geofence radius. Please select manually.</span>
            );
          })()}
        </div>
      )}

      <div className={styles.filters}>
        <label>Unloading Place:</label>
        <Select
          options={placeOptions}
          value={selectedPlace}
          onChange={(val) => {
            const same = val?.value === selectedPlace?.value;
          
            setSelectedPlace(val);
            setSelectionSource('manual');   // <-- manual choice
          
            if (same) {
              fetchTrucks(val?.value, val?.entity_code);
            } else {
              setTruckList([]);
              setSelectedTruck(null);
              setUnloadingMethod('Both');
              setRemarks('');
              
              // clear staged single photo when switching places
              if (photoPreview) URL.revokeObjectURL(photoPreview);
              setPhotoFile(null);
              setPhotoPreview(null);
            }
          }}
                    
          className={styles.select}
          styles={selectStyles} 
        />
      </div>

      <div className={styles.filters}>
        <label>Select Truck:</label>
        <Select
          options={truckList.map(getTruckOption)}
          value={getTruckOption(selectedTruck)}
          onChange={(option) => setSelectedTruck(option?.data || null)}
          className={styles.select}
          isDisabled={truckList.length === 0}
          placeholder={truckList.length === 0 ? 'No trucks available' : 'Select a truck'}
        />
      </div>

      {selectedTruck && (
        <div className="card p-3 mt-3">
          <h6>Truck Details</h6>
            <div className={styles.infoGrid} style={{ "--icon-size":"22px", "--text-size":"22px" }}>
              <Row
                icon="🚚"
                title="Truck No"
                style={{ "--icon-size":"26px", "--text-size":"22px" }}  // bigger Truck row
              >
                {selectedTruck.P_truck}
              </Row>
            </div>
            <div className={styles.infoGrid}>
              <Row icon="🏭 " title="Transporter">{selectedTruck.p_Trans_name}</Row>
              <Row icon="🆔 " title="Gate Entry No">{selectedTruck.p_docnumber}</Row>
              <Row icon="📦 " title="Item">{selectedTruck.p_item_name}</Row>
              <Row icon="⚖️ " title="Weight">{formatKg(selectedTruck.P_CHALLAN_WT)}</Row>
              <Row icon="🧑 " title="Supplier">{selectedTruck.P_PARTY_NAME}</Row>
              <Row icon="📍  " title="Mines">{selectedTruck.P_LOAD_POINT}</Row>
              <Row icon="🧪 " title="Limits">{selectedTruck.test_min_limits}</Row>
              <Row icon="📝 " title="Limits">{selectedTruck.p_pono}</Row>
              <Row icon="💰 " title="Limits">{selectedTruck.p_posorate}</Row>
              <Row icon="Landed Cost " title="Limits">{selectedTruck.deduct_rate}</Row>
              
          </div>
          <br></br>
          {/* Phase + GPS summary + Geofence notice */}
          <label>Submission Phase:</label>
          <div className={styles.unloadingMethods} style={{ marginBottom: 8 }}>
            {['before', 'after'].map(p => (
              <label key={p} style={{ textTransform: "capitalize" }}>
                <input
                  type="radio"
                  name="phase"
                  value={p}
                  checked={phase === p}
                  onChange={(e) => setPhase(e.target.value)}
                  required
                />
                {p}
              </label>
            ))}
          </div>

          <label>Unloading Method:</label>
          <div className={styles.unloadingMethods}>
            {['Labour', 'Machine', 'Both', 'Self'].map(method => (
              <label key={method}>
                <input
                  type="radio"
                  name="unloadingMethod"
                  value={method}
                  checked={unloadingMethod === method}
                  onChange={(e) => setUnloadingMethod(e.target.value)}
                  required
                />
                {method}
              </label>
            ))}
          </div>

          <label>QC Check Required?</label>
          <div className={styles.unloadingMethods}>
            {['Y','N'].map(v => (
              <label key={v} title={qcLocked && v==='N' ? 'QC is mandatory for this party/category' : ''}>
                <input
                  type="radio"
                  name="qcRequired"
                  value={v}
                  checked={qcRequired === v}
                  onChange={(e) => !qcLocked && setQcRequired(e.target.value)}
                  disabled={qcLocked && v === 'N'}   // lock the N option only
                  required
                />
                {v === 'Y' ? 'Yes' : 'No'}
              </label>
            ))}
          </div>

          <label>QC Remarks:</label>
          <textarea
            className="form-control"
            rows={2}
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
          />

          {/* Photos (DB + new), stamp-sized, and clickable */}
          <div className={`${styles.photoSection} ${styles.stamp}`}>
            <h6>Photos</h6>

            {(existing.before_url || existing.after_url) && (
              <div className={styles.photoGrid}>
                {existing.before_url && (
                  <div className={styles.photoCard}>
                    <div className={styles.photoLabel}>Before (from DB)</div>
                    <a href={existing.before_url} target="_blank" rel="noopener noreferrer" title="Open full image">
                      <img className={styles.photoThumb} src={existing.before_url} alt="Before (DB)" />
                    </a>
                    {existing.before_time && <small className={styles.photoMeta}>{existing.before_time}</small>}
                  </div>
                )}
                {existing.after_url && (
                  <div className={styles.photoCard}>
                    <div className={styles.photoLabel}>After (from DB)</div>
                    <a href={existing.after_url} target="_blank" rel="noopener noreferrer" title="Open full image">
                      <img className={styles.photoThumb} src={existing.after_url} alt="After (DB)" />
                    </a>
                    {existing.after_time && <small className={styles.photoMeta}>{existing.after_time}</small>}
                  </div>
                )}
              </div>
            )}

            <div className={styles.photoGrid}>
              <div className={styles.photoCard}>
                <div className={styles.photoLabel}>
                  {phase === 'after' ? 'After (new)' : 'Before (new)'}
                </div>

                {photoPreview ? (
                  <a href={photoPreview} target="_blank" rel="noopener noreferrer" title="Open full image">
                    <img className={styles.photoThumb} src={photoPreview} alt={`${phase} preview`} />
                  </a>
                ) : (
                  <div className={styles.photoEmpty}>No file chosen</div>
                )}

                {/* Native picker (disabled on Xiaomi if useInAppCam === true) */}
                {!useInAppCam && (
                  <input
                    key={phase}                // ensures a fresh picker per phase
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onClick={() => saveCheckpoint()}
                    onChange={async (e) => {
                      const f = e.target.files?.[0];
                      if (!f && isXiaomiLike) {
                        setUseInAppCam(true);
                        setShowInAppCam(true);
                        return;
                      }
                      if (f) await onPhotoPick(e);
                    }}
                  />
                )}

                {/* In-app camera (default on Xiaomi/HyperOS) */}
                {useInAppCam && (
                  <button
                    type="button"
                    disabled={showInAppCam}
                    onClick={() => { saveCheckpoint(); setShowInAppCam(true); }}
                  >
                    Open Camera
                  </button>
                )}
              </div>
            </div>

            {showInAppCam && (
              <InAppCamera
                onCancel={() => setShowInAppCam(false)}
                onCapture={async (file) => {
                  try {
                    const blob = await compressToJpegBlob(file, 1280, 0.8);
                    const named = new File([blob], phase === 'after' ? 'after.jpg' : 'before.jpg', { type: 'image/jpeg' });
                    if (photoPreview) URL.revokeObjectURL(photoPreview);
                    setPhotoFile(named);
                    setPhotoPreview(URL.createObjectURL(named));
                  } finally {
                    setShowInAppCam(false);
                  }
                }}
              />
            )}
          </div>

          <br />
          <button className={styles.approveBtn} onClick={handleSubmit}>
            Submit Unloading Status
          </button>
        </div>
      )}

      <button className={styles.cancelBtn} onClick={() => navigate('/dashboard')}>
        Cancel
      </button>
    </div>
  );
};

export default UnloadingEntry;
